import { Button } from "@frontend/components/ui/button"
import type { EditRegionItem } from "@frontend/pages/case-workspace/pathology/lib/module-edit-regions"
import { ChevronRight, Crosshair, Eye, EyeOff } from "lucide-react"

interface EditRegionsCardProps {
  /** Edit regions of the active slide; the card renders nothing for `null` / empty. */
  regions: EditRegionItem[] | null
  /** What the regions limit, for the tooltips (e.g. "Editing", "Drawing"). */
  activity?: string
  /**
   * The regions' dim / outlines are hidden in the viewer so the slide can be seen
   * unobstructed. Visual only: editing stays confined to the regions.
   */
  hidden: boolean
  onHiddenChange?: (hidden: boolean) => void
  onJumpToRegion?: (key: string) => void
  onNextRegion?: () => void
}

/** Regions card shared by the module panels that support ROI-restricted editing. */
export function EditRegionsCard({
  regions,
  activity = "Editing",
  hidden,
  onHiddenChange,
  onJumpToRegion,
  onNextRegion
}: EditRegionsCardProps) {
  if (!regions || regions.length === 0) return null
  const lowerActivity = activity.toLowerCase()
  return (
    <div
      className="shrink-0 space-y-1.5 rounded-md border border-border bg-surface-raised px-2.5 py-2"
      data-testid="edit-regions-card"
    >
      <div className="flex items-center justify-between gap-2">
        <div
          className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide"
          title={
            hidden
              ? `${activity} is limited to these regions. Their outlines and the dimming are hidden in the viewer.`
              : `${activity} is limited to these regions; everything else is dimmed in the viewer.`
          }
        >
          Regions
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] text-muted-foreground tabular-nums">{regions.length}</span>
          {onHiddenChange && (
            <button
              type="button"
              className="text-muted-foreground hover:text-foreground p-0.5 rounded shrink-0"
              title={
                hidden
                  ? "Show the region outlines and dim the rest of the slide"
                  : `Hide the region outlines and dimming (${lowerActivity} stays limited to the regions)`
              }
              aria-label={hidden ? "Show regions" : "Hide regions"}
              aria-pressed={hidden}
              data-testid="edit-regions-visibility-toggle"
              onClick={() => onHiddenChange(!hidden)}
            >
              {hidden ? <EyeOff className="size-3" /> : <Eye className="size-3" />}
            </button>
          )}
          {onNextRegion && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-6 px-1.5 text-[10px]"
              title="Jump to the next region"
              onClick={onNextRegion}
            >
              Next
              <ChevronRight className="size-3" />
            </Button>
          )}
        </div>
      </div>
      <div className="max-h-40 overflow-auto space-y-0.5 pr-0.5" data-testid="edit-regions-list">
        {regions.map((region) => (
          <div
            key={region.key}
            className="flex items-center gap-1.5 rounded-md border border-transparent px-1.5 py-1 hover:bg-accent/40"
            title={region.note ?? undefined}
          >
            <div className="flex-1 min-w-0">
              <div className="text-[11px] truncate">{region.label}</div>
              <div className="text-[9px] text-muted-foreground tabular-nums truncate">
                {region.width.toLocaleString()} × {region.height.toLocaleString()} px
                {region.note ? ` · ${region.note}` : ""}
              </div>
            </div>
            {onJumpToRegion && (
              <Button
                type="button"
                size="icon-xs"
                variant="ghost"
                aria-label={`Go to ${region.label}`}
                title={`Go to ${region.label}`}
                onClick={() => onJumpToRegion(region.key)}
              >
                <Crosshair className="size-3" />
              </Button>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
